import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ActionButtonVariants } from 'components/action-button';
import { useFeatureLoyaltyRewardListCategoriesOnly } from 'hooks/use-feature-loyalty-reward-list-categories-only';
import { useFeatureLoyaltyUi } from 'hooks/use-feature-loyalty-ui';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { PromoMessage } from './promo-message';
import {
  AuthenticatedContainer,
  AvailableCrownsCaption,
  CurrentProgress,
  DashboardButton,
  PointsContainer,
  PointsProgress,
  ProgressBarIcon,
  StyledLoyaltyPointsIcon,
  TierDot,
  UserCrownPointsContainer,
} from './styled';
import { getProgressBarValue, mapCategoriesToTiers, sanitizeStringToNumber } from './utils';

export interface BannerTier {
  id: string;
  value: string;
}

const INITIAL_TIER_VALUE = 0;

const AuthenticatedBanner: FC = () => {
  const {
    data: categories,
    loading: loadingCategories,
  } = useFeatureLoyaltyRewardListCategoriesOnly();
  const { featureLoyaltyUiLoading, featureLoyaltyUi } = useFeatureLoyaltyUi();
  const hasIcon = !featureLoyaltyUiLoading && featureLoyaltyUi?.rewardsLearnMoreIcon?.locale;

  const { loyaltyUser } = useLoyaltyUser();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const redeemClick = () => {
    navigate(formatMessage({ id: 'routes.loyaltyBannerAuthenticated' }));
  };

  if (loadingCategories || !categories?.length || !loyaltyUser) {
    return null;
  }
  const bestPromoId = loyaltyUser.bestPromotion?.sanityId;
  const userPoints = loyaltyUser.points || 0;
  const tiers: BannerTier[] = mapCategoriesToTiers(categories);

  return (
    <AuthenticatedContainer data-testid="authenticated-banner" hasPromoMessage={!!bestPromoId}>
      {bestPromoId && <PromoMessage promoId={bestPromoId} />}
      <PointsContainer>
        <UserCrownPointsContainer>
          <StyledLoyaltyPointsIcon width="50" />
          <span data-testid="authenticated-banner-points">{userPoints}</span>
        </UserCrownPointsContainer>
        <AvailableCrownsCaption>{formatMessage({ id: 'availableCrowns' })}</AvailableCrownsCaption>
      </PointsContainer>
      <DashboardButton variant={ActionButtonVariants.OUTLINE} onClick={redeemClick}>
        {formatMessage({ id: 'loyaltyBannerDashboardButtonLabel' })}
      </DashboardButton>
      <PointsProgress>
        <TierDot
          completed={userPoints > INITIAL_TIER_VALUE}
          value={INITIAL_TIER_VALUE.toString()}
        />

        {tiers.map((tier: BannerTier, index) => {
          const left = (100 / tiers.length) * (index + 1);
          const completed = sanitizeStringToNumber(tier.value) <= userPoints;
          return (
            <React.Fragment key={tier.id}>
              <TierDot left={left} completed={completed} value={tier.value} />
            </React.Fragment>
          );
        })}
        <CurrentProgress $progress={getProgressBarValue(userPoints, tiers)}>
          {hasIcon && (
            <ProgressBarIcon
              $progress={getProgressBarValue(userPoints, tiers)}
              image={featureLoyaltyUi?.rewardsProgressBarIcon?.locale}
              alt=""
              objectFitContain
            />
          )}
        </CurrentProgress>
      </PointsProgress>
    </AuthenticatedContainer>
  );
};

export default AuthenticatedBanner;
